import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '@/lib/api';
import type * as API from '@/types/api';
import { useAccountStore } from '@nimbitlabs/features';

interface DeploymentsState {
  deployments: API.DeploymentDto[];
  loading: boolean;
  error: string | null;
  
  fetchDeployments: () => Promise<void>;
  addDeployment: (deploymentData: API.CreateDeploymentDto) => Promise<API.DeploymentDto>;
  updateDeployment: (id: string, deploymentData: API.UpdateDeploymentDto) => Promise<void>;
  deleteDeployment: (deploymentId: string) => Promise<void>;
  getDeploymentById: (id: string) => Promise<API.DeploymentDto>;
  clearError: () => void;
}

const deploymentsStore = create<DeploymentsState>()(
  subscribeWithSelector((set, get) => ({
  deployments: [],
  loading: false,
  error: null,
  
  fetchDeployments: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.deployments.getAll();
      set({ deployments: data, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch deployments', loading: false });
    }
  },
  
  addDeployment: async (deploymentData: API.CreateDeploymentDto) => {
    const tempId = `temp-${Date.now()}`;
    const optimisticDeployment: API.DeploymentDto = {
      id: tempId,
      ...deploymentData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    set(state => ({ deployments: [optimisticDeployment, ...state.deployments] }));

    try {
      const created = await api.deployments.create(deploymentData);
      set(state => ({
        deployments: state.deployments.map(d => d.id === tempId ? created : d)
      }));
      return created;
    } catch (error) {
      set(state => ({
        deployments: state.deployments.filter(d => d.id !== tempId),
        error: error instanceof Error ? error.message : 'Failed to create deployment'
      }));
      throw error;
    }
  },
  
  updateDeployment: async (id: string, deploymentData: API.UpdateDeploymentDto) => {
    const original = get().deployments.find(d => d.id === id);
    
    set(state => ({
      deployments: state.deployments.map(d => 
        d.id === id ? { ...d, ...deploymentData, updatedAt: new Date().toISOString() } : d
      )
    }));
    
    try {
      const updated = await api.deployments.update(id, deploymentData);
      set(state => ({
        deployments: state.deployments.map(d => d.id === id ? updated : d)
      }));
    } catch (error) {
      if (original) {
        set(state => ({
          deployments: state.deployments.map(d => d.id === id ? original : d)
        }));
      }
      throw error;
    }
  },
  
  deleteDeployment: async (deploymentId: string) => {
    const original = get().deployments;
    
    set(state => ({
      deployments: state.deployments.filter(d => d.id !== deploymentId)
    }));
    
    try {
      await api.deployments.delete(deploymentId);
    } catch (error) {
      set({ deployments: original });
      throw error;
    }
  },
  
  getDeploymentById: async (id: string) => {
    const cached = get().deployments.find(d => d.id === id);
    if (cached) return cached;
    
    const data = await api.deployments.getById(id);
    set(state => ({
      deployments: [...state.deployments.filter(d => d.id !== id), data]
    }));
    return data;
  },
  
  clearError: () => set({ error: null }),
  }))
);

export const useDeploymentsStore = deploymentsStore;

// Subscribe to account changes and auto-fetch
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      deploymentsStore.getState().fetchDeployments();
    } else {
      deploymentsStore.setState({ deployments: [] });
    }
  }
);
